import type { VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { Button, type buttonVariants } from '../../components/ui/button';

type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export type FormSubmitButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> & {
  variant?: VariantProps<typeof buttonVariants>['variant'];
  shape?: VariantProps<typeof buttonVariants>['shape'];
  size?: Exclude<ButtonSize, 'icon'>;
  /** Also disable while the form is pristine. Off by default. */
  requireDirty?: boolean;
  /** Also disable while the form is invalid (RHF `isValid`). Off by default. */
  requireValid?: boolean;
  /** Override the derived pending state: `<Form action>` runs its handler synchronously, so `isSubmitting` misses a kicked-off `mutate`. */
  loading?: boolean;
  children: ReactNode;
  ref?: Ref<HTMLButtonElement>;
};

/**
 * Submit button for forms wrapped in `<Form>` (or any `FormProvider`). Reads
 * `isSubmitting` / `isDirty` from context, so callers don't wire pending or
 * dirty state themselves. Throws if used outside a form context.
 */
export function FormSubmitButton({
  requireDirty = false,
  requireValid = false,
  loading,
  disabled,
  children,
  ...rest
}: FormSubmitButtonProps) {
  const methods = useFormContext();
  if (!methods) {
    throw new Error('FormSubmitButton must be used inside a <Form> (or react-hook-form FormProvider).');
  }
  const { isSubmitting, isDirty, isValid } = useFormState({ control: methods.control });
  const pending = loading ?? isSubmitting;

  return (
    <Button
      {...rest}
      type="submit"
      loading={pending}
      disabled={pending || (requireDirty && !isDirty) || (requireValid && !isValid) || disabled}
    >
      {children}
    </Button>
  );
}
